import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Card,
  Badge,
  Form,
  Row,
  Col,
  Button,
  InputGroup,
  Alert,
  Spinner,
  Tabs,
  Tab,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layouts/Main";
import config from "../../config";

import { useHasPermission, PERMISSIONS } from "../../utils/permissions";
import axios from "axios";
import { formatDateTime } from "../../utils/dateHelpers";
import StockTable from "../../components/stock/StockTable";


const InventoryList = () => {
  const [inventoryData, setInventoryData] = useState({
    products: [],
    lastUpdated: null
  });
  const [pmInventoryData, setPmInventoryData] = useState({
    products: [],
    lastUpdated: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reservedStock, setReservedStock] = useState({});
  const [filters, setFilters] = useState({
    search: "",
    stockLevel: "all",
    category: "",
  });
  const [pmFilters, setPmFilters] = useState({
    search: "",
    stockLevel: "all",
  });

  // Permission checks
  const canViewInventory = useHasPermission(PERMISSIONS.INVENTORY_VIEW);
  // const canEditInventory = useHasPermission(PERMISSIONS.INVENTORY_EDIT);

  // Group products by their groupId
  const groupedProducts = useMemo(() => {
    return inventoryData.products.reduce((acc, product) => {
      const groupId = product.product.groupId?._id || 'ungrouped';
      const groupName = product.product.groupId?.groupName || 'Ungrouped';

      if (!acc[groupId]) {
        acc[groupId] = {
          _id: groupId,
          groupName: groupName,
          products: []
        };
      }
      acc[groupId].products.push(product);
      return acc;
    }, {});
  }, [inventoryData.products]);

  const getStockStatus = (availableStock, bufferStock) => {
    if (availableStock <= 0) {
      return { label: 'Out of Stock', color: 'danger' };
    }
    if (availableStock <= bufferStock) {
      return { label: 'Low Stock', color: 'warning' };
    }
    return { label: 'In Stock', color: 'success' };
  };

  const fetchStockData = useCallback(async () => {
    if (!canViewInventory) {
      toast.error("You don't have permission to view inventory");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [productResponse, pmResponse] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/inventory/stock-overview`),
        axios.get(`${config.API_BASE_URL}/pm-inventory/stock-overview`)
      ]);

      setInventoryData({
        products: productResponse.data.products,
        lastUpdated: productResponse.data.lastUpdated
      });
      setReservedStock(productResponse.data.reservedStock || {});

      setPmInventoryData({
        products: pmResponse.data.products || [],
        lastUpdated: pmResponse.data.lastUpdated
      });
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch stock data');
      toast.error("Failed to load inventory data");
    } finally {
      setLoading(false);
    }
  }, [canViewInventory]);

  useEffect(() => {
    fetchStockData();
  }, [fetchStockData]);

  const filteredInventory = useMemo(() => {
    if (!inventoryData.products) return [];

    let items = inventoryData.products;

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      items = items.filter(
        (item) =>
          item.product?.productName?.toLowerCase().includes(searchTerm) ||
          item.product?.sku?.toLowerCase().includes(searchTerm) ||
          item.product?.productCode?.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.stockLevel !== "all") {
      items = items.filter((item) => {
        const currentStock = item.totalAvailableStock;
        switch (filters.stockLevel) {
          case "low":
            return currentStock > 0 && currentStock <= 100;
          case "out":
            return currentStock === 0;
          case "normal":
            return currentStock > 100;
          default:
            return true;
        }
      });
    }

    if (filters.category) {
      items = items.filter((item) =>
        item.product?.category
          ?.toLowerCase()
          .includes(filters.category.toLowerCase())
      );
    }

    return items;
  }, [inventoryData.products, filters]);



  const filteredPmInventory = useMemo(() => {
    if (!pmInventoryData.products) return [];

    let items = pmInventoryData.products;

    if (pmFilters.search) {
      const searchTerm = pmFilters.search.toLowerCase();
      items = items.filter(
        (item) =>
          item.packingMaterial?.name?.toLowerCase().includes(searchTerm) ||
          item.packingMaterial?.alias?.toLowerCase().includes(searchTerm)
      );
    }

    if (pmFilters.stockLevel !== "all") {
      items = items.filter((item) => {
        const currentStock = item.totalAvailableStock || 0;
        const reorderLevel = item.packingMaterial?.reorderQuantity || 0;
        switch (pmFilters.stockLevel) {
          case "low":
            return currentStock > 0 && currentStock <= reorderLevel;
          case "out":
            return currentStock === 0;
          case "normal":
            return currentStock > reorderLevel;
          default:
            return true;
        }
      });
    }

    return items;
  }, [pmInventoryData.products, pmFilters]);



  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePmFilterChange = (key, value) => {
    setPmFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: "", stockLevel: "all", category: "" });
  };

  const clearPmFilters = () => {
    setPmFilters({ search: "", stockLevel: "all" });
  };

  // If user can't view inventory, show permission denied
  if (!canViewInventory) {
    return (
      <Layout>
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Inventory</li>
          </ol>
        </nav>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to view inventory. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <Container fluid className="py-4">
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading inventory...</p>
          </div>
        </Container>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container fluid className="py-4">
          <Alert variant="danger">
            <Alert.Heading>Error</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" onClick={fetchStockData}>
              Try Again
            </Button>
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Dashboard</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/inventory">Inventory</Link>
          </li>
        </ol>
      </nav>
      <Container fluid className="py-4">
        <Row className="mb-3">
          <Col>
            <h2>Stock Overview</h2>
          </Col>
          <Col className="text-end">
            <small className="text-muted">
              Last Updated: {formatDateTime(inventoryData.lastUpdated)}
            </small>
          </Col>
        </Row>
 
        <Tabs defaultActiveKey="stock" className="mb-3">
          <Tab eventKey="stock" title="Product Stock">
            <Card>
              <Card.Body>
                {/* Filters */}
                <Row className="mb-4">
                  <Col md={4}>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Search products..."
                        value={filters.search}
                        onChange={(e) =>
                          handleFilterChange("search", e.target.value)
                        }
                      />
                      <Button variant="outline-secondary" onClick={clearFilters}>
                        <i className="fas fa-times"></i>
                      </Button>
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Form.Select
                      value={filters.stockLevel}
                      onChange={(e) =>
                        handleFilterChange("stockLevel", e.target.value)
                      }
                    >
                      <option value="all">All Stock Levels</option>
                      <option value="normal">Normal Stock</option>
                      <option value="low">Low Stock</option>
                      <option value="out">Out of Stock</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Control
                      type="text"
                      placeholder="Filter by category..."
                      value={filters.category}
                      onChange={(e) =>
                        handleFilterChange("category", e.target.value)
                      }
                    />
                  </Col>
                </Row>

                {/* Stock Statistics */}
                <Row className="mb-3">
                  <Col md={3}>
                    <div className="text-center p-2 bg-light rounded">
                      <div className="fw-bold">{filteredInventory.length}</div>
                      <small className="text-muted">Filtered Items</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center p-2 bg-success bg-opacity-10 rounded">
                      <div className="fw-bold text-success">
                        {filteredInventory.filter(item => {
                          const currentStock = item.totalAvailableStock;
                          return currentStock > 100;
                        }).length}
                      </div>
                      <small className="text-muted">Normal Stock</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center p-2 bg-warning bg-opacity-10 rounded">
                      <div className="fw-bold text-warning">
                        {filteredInventory.filter(item => {
                          const currentStock = item.totalAvailableStock;
                          return currentStock > 0 && currentStock <= 100;
                        }).length}
                      </div>
                      <small className="text-muted">Low Stock</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center p-2 bg-danger bg-opacity-10 rounded">
                      <div className="fw-bold text-danger">
                        {filteredInventory.filter(item => item.totalAvailableStock === 0).length}
                      </div>
                      <small className="text-muted">Out of Stock</small>
                    </div>
                  </Col>
                </Row>

                {/* Alerts */}
                {filteredInventory.filter(item => item.totalAvailableStock === 0).length > 0 && (
                  <Alert variant="danger" className="mb-3">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {filteredInventory.filter(item => item.totalAvailableStock === 0).length} filtered item(s) are out of stock!
                  </Alert>
                )}
                {filteredInventory.filter(item => {
                  const currentStock = item.totalAvailableStock;
                  return currentStock > 0 && currentStock <= 100;
                }).length > 0 && (
                  <Alert variant="warning" className="mb-3">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {filteredInventory.filter(item => {
                      const currentStock = item.totalAvailableStock;
                      return currentStock > 0 && currentStock <= 100;
                    }).length} filtered item(s) have low stock levels.
                  </Alert>
                )}

                <StockTable
                  groupedProducts={groupedProducts}
                  reservedStock={reservedStock}
                  getStockStatus={getStockStatus}
                  filters={filters}
                />
              </Card.Body>
            </Card>
          </Tab>
          <Tab eventKey="packing-materials" title="Packing Materials">
            <Card>
              <Card.Body>
                {/* Filters */}
                <Row className="mb-4">
                  <Col md={4}>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Search packing materials..."
                        value={pmFilters.search}
                        onChange={(e) =>
                          handlePmFilterChange("search", e.target.value)
                        }
                      />
                      <Button variant="outline-secondary" onClick={clearPmFilters}>
                        <i className="fas fa-times"></i>
                      </Button>
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Form.Select
                      value={pmFilters.stockLevel}
                      onChange={(e) =>
                        handlePmFilterChange("stockLevel", e.target.value)
                      }
                    >
                      <option value="all">All Stock Levels</option>
                      <option value="normal">Normal Stock</option>
                      <option value="low">Low Stock</option>
                      <option value="out">Out of Stock</option>
                    </Form.Select>
                  </Col>
                </Row>

                {/* PM Stock Statistics */}
                <Row className="mb-3">
                  <Col md={3}>
                    <div className="text-center p-2 bg-light rounded">
                      <div className="fw-bold">{filteredPmInventory.length}</div>
                      <small className="text-muted">Filtered Items</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center p-2 bg-success bg-opacity-10 rounded">
                      <div className="fw-bold text-success">
                        {filteredPmInventory.filter(item => {
                          const currentStock = item.totalAvailableStock || 0;
                          const reorderLevel = item.packingMaterial?.reorderQuantity || 0;
                          return currentStock > reorderLevel;
                        }).length}
                      </div>
                      <small className="text-muted">Normal Stock</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center p-2 bg-warning bg-opacity-10 rounded">
                      <div className="fw-bold text-warning">
                        {filteredPmInventory.filter(item => {
                          const currentStock = item.totalAvailableStock || 0;
                          const reorderLevel = item.packingMaterial?.reorderQuantity || 0;
                          return currentStock > 0 && currentStock <= reorderLevel;
                        }).length}
                      </div>
                      <small className="text-muted">Low Stock</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center p-2 bg-danger bg-opacity-10 rounded">
                      <div className="fw-bold text-danger">
                        {filteredPmInventory.filter(item => (item.totalAvailableStock || 0) === 0).length}
                      </div>
                      <small className="text-muted">Out of Stock</small>
                    </div>
                  </Col>
                </Row>

                {/* PM Alerts */}
                {filteredPmInventory.filter(item => (item.totalAvailableStock || 0) === 0).length > 0 && (
                  <Alert variant="danger" className="mb-3">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {filteredPmInventory.filter(item => (item.totalAvailableStock || 0) === 0).length} filtered packing material(s) are out of stock!
                  </Alert>
                )}
                {filteredPmInventory.filter(item => {
                  const currentStock = item.totalAvailableStock || 0;
                  const reorderLevel = item.packingMaterial?.reorderQuantity || 0;
                  return currentStock > 0 && currentStock <= reorderLevel;
                }).length > 0 && (
                  <Alert variant="warning" className="mb-3">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {filteredPmInventory.filter(item => {
                      const currentStock = item.totalAvailableStock || 0;
                      const reorderLevel = item.packingMaterial?.reorderQuantity || 0;
                      return currentStock > 0 && currentStock <= reorderLevel;
                    }).length} filtered packing material(s) have low stock levels.
                  </Alert>
                )}

                {/* PM Stock Table */}
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Packing Material</th>
                        <th>Available Stock</th>
                        <th>Unit</th>
                        <th>Reorder Level</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPmInventory.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <div className="text-muted">No packing materials found</div>
                          </td>
                        </tr>
                      ) : (
                        filteredPmInventory.map((item) => {
                          const currentStock = item.totalAvailableStock || 0;
                          const reorderLevel = item.packingMaterial?.reorderQuantity || 0;
                          const isOutOfStock = currentStock === 0;
                          const isLowStock = currentStock > 0 && currentStock <= reorderLevel;

                          return (
                            <tr key={item.packingMaterial._id}>
                              <td>
                                <div>
                                  <strong className="text-primary">{item.packingMaterial.name}</strong>
                                  <br />
                                  <small className="text-muted">{item.packingMaterial.alias} - {item.packingMaterial.size}</small>
                                </div>
                              </td>
                              <td>
                                <span className={`fw-semibold ${isOutOfStock ? 'text-danger' : isLowStock ? 'text-warning' : 'text-success'}`}>
                                  {currentStock}
                                </span>
                              </td>
                              <td>{item.packingMaterial.quantityUnit || 'pcs'}</td>
                              <td>{reorderLevel}</td>
                              <td>
                                <Badge bg={isOutOfStock ? 'danger' : isLowStock ? 'warning' : 'success'}>
                                  {isOutOfStock ? 'Out of Stock' : isLowStock ? 'Low Stock' : 'In Stock'}
                                </Badge>
                              </td>
                              <td>
                                <Link
                                  to={`/packaging-material-stock-details/${item.packingMaterial._id}`}
                                  className="btn btn-outline-primary btn-sm"
                                >
                                  <i className="fas fa-eye me-1"></i>View Details
                                </Link>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Tab>
          {/* <Tab eventKey="orders" title={
            <div className="d-flex align-items-center gap-2">
              Sales Orders
              <Badge bg="secondary">
                {salesOrders.new.length + salesOrders.inProduction.length + salesOrders.dispatched.length}
              </Badge>
            </div>
          }>
            <SalesOrdersOverview orders={salesOrders} />
          </Tab> */}
        </Tabs>
      </Container>
    </Layout>
  );
};

export default InventoryList;
